
import { useGeolocation } from "vue-composable";
import Camera from "simple-vue-camera";
import { computed, ref, onMounted, onUnmounted } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useToast } from "primevue/usetoast";

interface GpsSample {
  lat: number;
  lng: number;
  ts: number;
}

export default {
  setup() {
    const camera = ref<InstanceType<typeof Camera>>();
    const toast = useToast();
    const store = useStore();
    const router = useRouter();

    const loadingBar = ref(false);
    const cameraPermission = ref(true);

    /* ================= GEO ================= */
    const geo = useGeolocation({
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 15000
    });

    const latitude = computed(() => geo.coords.value?.latitude ?? null);
    const longitude = computed(() => geo.coords.value?.longitude ?? null);
    const accuracy = computed(() => geo.coords.value?.accuracy ?? null);
    const timestamp = computed(() => geo.timestamp.value ?? null);

    /* ================= GPS SAMPLES ================= */
    const samples = ref<GpsSample[]>([]);
    const watchId = ref<number | null>(null);
    const collecting = ref(false);

    const startCollect = () => {
      if (!navigator.geolocation) {
        toast.add({
          severity: "error",
          summary: "Thiết bị không hỗ trợ GPS",
          life: 3000
        });
        return;
      }

      samples.value = [];
      collecting.value = true;

      watchId.value = navigator.geolocation.watchPosition(
          pos => {
            samples.value.push({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              ts: Date.now()
            });

            // đủ mẫu thì dừng
            if (samples.value.length >= 5 && watchId.value !== null) {
              navigator.geolocation.clearWatch(watchId.value);
              watchId.value = null;
              collecting.value = false;
            }
          },
          err => {
            console.error(err);
            collecting.value = false;
          },
          {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 10000
          }
      );
    };

    onMounted(() => {
      // ⭐ TỰ ĐỘNG COLLECT – FIX LỖI KHÔNG PASS
      startCollect();
    });

    onUnmounted(() => {
      if (watchId.value !== null) {
        navigator.geolocation.clearWatch(watchId.value);
      }
    });

    /* ================= READY (NỚI LỎNG HỢP LÝ) ================= */
    const gpsReady = computed(() => {
      return (
          latitude.value !== null &&
          longitude.value !== null &&
          accuracy.value !== null &&
          samples.value.length >= 2 && // ⭐ chỉ cần 2 mẫu
          accuracy.value <= 150
      );
    });

    const accuracyLevel = computed(() => {
      if (accuracy.value === null) return "unknown";
      if (accuracy.value <= 20) return "🟢 excellent";
      if (accuracy.value <= 50) return "🟡 good";
      if (accuracy.value <= 150) return "🟠 acceptable";
      return "🔴 poor";
    });

    /* ================= SNAPSHOT ================= */
    const snapshot = async () => {
      // 🚨 CHECK NHẸ – KHÔNG CHẶN NGƯỜI THẬT
      if (samples.value.length < 2) {
        toast.add({
          severity: "info",
          summary: "Đang xác thực GPS",
          detail: "Vui lòng giữ máy ổn định trong 3–5 giây",
          life: 3000
        });
        return;
      }

      if (accuracy.value !== null && accuracy.value > 150) {
        toast.add({
          severity: "warn",
          summary: "Độ chính xác GPS thấp",
          detail: "Vui lòng bật GPS chính xác hoặc ra ngoài trời",
          life: 4000
        });
        return;
      }

      const blob = await camera.value?.snapshot(
          { width: 480, height: 270 },
          "image/jpeg",
          0.7
      );

      if (!blob) return;

      const form = new FormData();
      form.append("formFiles", new File([blob], "checkin.jpg"));
      form.append("accuracy", String(accuracy.value));
      form.append("clientTimestamp", String(Date.now()));
      form.append("samples", JSON.stringify(samples.value));

      loadingBar.value = true;
      axios.defaults.headers.common = {
        Authorization: `Bearer ${store.state.token}`
      };

      axios
          .post(
              `/api/file/UploadCheckIn/${longitude.value}/${latitude.value}`,
              form,
              { headers: { "Content-Type": "multipart/form-data" } }
          )
          .then(() => {
            loadingBar.value = false;
            toast.add({
              severity: "success",
              summary: "Check-in thành công",
              life: 3000
            });
          })
          .catch(err => {
            loadingBar.value = false;
            if (err.response?.status === 401) {
              store.dispatch("clearToken");
              router.push("login");
            } else {
              toast.add({
                severity: "error",
                summary: "Lỗi",
                detail: err.response?.data ?? "Upload thất bại",
                life: 4000
              });
            }
          });
    };

    return {
      camera,
      snapshot,
      startCollect,
      collecting,
      gpsReady,
      latitude,
      longitude,
      accuracy,
      accuracyLevel,
      timestamp,
      samples,
      loadingBar,
      cameraPermission
    };
  }
};
